import { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { message } from "antd";
import { apiClient } from "../config/api.client";
import "./css/PaymentStatus.css";

const formatDateTime = (isoString) => {
    if (!isoString) return "";
    try {
        const date = new Date(isoString);
        return date.toLocaleString();
    } catch (_) {
        return isoString;
    }
};

const useQuery = () => {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
};

const PaymentStatus = () => {
    const query = useQuery();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);
    const fbqFiredRef = useRef(false);

    const clientTxnId = query.get("client_txn_id") || query.get("clientTxnId") || query.get("orderId");
    const txnId = query.get("client_txn_id") || query.get("clientTxnId") || query.get("orderId");

    const fetchByWalletStatus = async (gatewayTxnId) => {
        const url = `/wallet/payment-status/${gatewayTxnId}`;
        const res = await apiClient.get(url);
        return res.data;
    };

    const fetchByTransactionStatus = async (clientId, gatewayId) => {
        const url = `/transaction/status`;
        const res = await apiClient.get(url, { params: { client_txn_id: clientId, txn_id: gatewayId } });
        return res.data;
    };

    const loadStatus = async () => {
        setLoading(true);
        setError("");
        try {
            let response;
            if (txnId) {
                response = await fetchByWalletStatus(txnId);
            } else if (clientTxnId && txnId) {
                response = await fetchByTransactionStatus(clientTxnId, txnId);
            } else if (clientTxnId) {
                // If only client_txn_id is present, try transaction status endpoint; backend may infer
                response = await fetchByTransactionStatus(clientTxnId, undefined);
            } else {
                throw new Error("Missing required query parameters");
            }

            if (response?.success) {
                setData(response.data);
            } else {
                throw new Error(response?.message || "Unable to fetch payment status");
            }
        } catch (err) {
            const apiMsg = err?.response?.data?.message || err.message || "Something went wrong";
            setError(apiMsg);
            message.error(apiMsg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStatus();
    }, [clientTxnId, txnId]);

    // Fire Meta Pixel purchase event once on successful wallet top-up
    useEffect(() => {
        if (!loading && !error && data && String(data.status || "").toLowerCase() === "success" && !fbqFiredRef.current) {
            const amountNumber = typeof data.amount === "number" ? data.amount : parseFloat(String(data.amount || "0").replace(/[^0-9.]/g, "")) || 0;
            if (typeof window !== "undefined" && typeof window.fbq !== "undefined") {
                try {
                    // Use transaction ID or order ID as deduplication key
                    const deduplicationId = data.txnId || data.gatewayOrderId || data.orderId || clientTxnId || txnId;
                    window.fbq("track", "Purchase", {
                        value: amountNumber,
                        currency: "INR",
                        payment_method: "wallet-topup",
                    }, {
                        eventID: deduplicationId ? `wallet_${deduplicationId}` : undefined
                    });
                    fbqFiredRef.current = true;
                } catch (_) {
                    // ignore pixel errors
                }
            }
        }
    }, [loading, error, data]);

    const getStatusIcon = (status) => {
        switch (status) {
            case "success":
                return (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                );
            case "failed":
                return (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 14L12 12M12 12L14 10M12 12L10 10M12 12L14 14M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                );
            case "pending":
                return (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 6V12L16 14M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                );
            default:
                return (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.228 9C8.43686 8.41645 8.81372 7.91998 9.30729 7.57371C9.80087 7.22744 10.3897 7.04637 10.9904 7.05705C11.5911 7.06773 12.1728 7.26972 12.6554 7.63003C13.1381 7.99034 13.4994 8.49718 13.6914 9.08527C13.8834 9.67336 13.8981 10.3088 13.7333 10.9054C13.5684 11.5019 13.2313 12.0267 12.7679 12.4134C12.3044 12.8001 11.7363 13.0291 11.1387 13.0699C10.5411 13.1107 9.94707 12.9619 9.44407 12.6437M12 17H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                );
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case "success":
                return "success";
            case "failed":
                return "failed";
            case "pending":
                return "pending";
            default:
                return "pending";
        }
    };

    return (
        <div className="payment-status-container">
            <div className="container">
                <div className="row justify-content-center">
                    <div className="">
                        {loading && (
                            <div className="payment-status-card">
                                <div className="loading-container">
                                    <div className="loading-spinner"></div>
                                    <h4>Loading Payment Status...</h4>
                                    <p>Please wait while we fetch your transaction details</p>
                                </div>
                            </div>
                        )}
                        
                        {!loading && error && (
                            <div className="payment-status-card">
                                <div className="error-container">
                                    <div className="error-icon">
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                    <div className="error-message">{error}</div>
                                    <button className="btn btn-custom btn-retry" onClick={loadStatus}>
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        {!loading && !error && data && (
                            <div className="payment-status-card">
                                <div className="payment-status-header">
                                    <div className={`status-icon ${getStatusClass(data.status)}`}>
                                        {getStatusIcon(data.status)}
                                    </div>
                                    <h1 className="payment-status-title">
                                        Payment {data.status?.charAt(0).toUpperCase() + data.status?.slice(1) || "Status"}
                                    </h1>
                                    <p className="payment-status-subtitle">
                                        {data.paymentNote || "Transaction details"}
                                    </p>
                                    <span className={`status-badge ${getStatusClass(data.status)}`}>
                                        {data.status || "pending"}
                                    </span>
                                </div>
                                
                                <div className="payment-details">
                                    <div className="details-table">
                                        <div className="detail-item">
                                            <span className="detail-label">Ord ID</span>
                                            <span className="detail-value">{(data.orderId).slice(4) || "-"}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Transaction ID</span>
                                            <span className="detail-value">{data.txnId || data.gatewayOrderId || "-"}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Amount</span>
                                            <span className="detail-value">
                                                {typeof data.amount === "number" ? `₹${data.amount.toLocaleString()}` : data.amount || "-"}
                                            </span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Customer</span>
                                            <span className="detail-value">
                                                {data.customerName || "-"}
                                                {data.customerNumber && (
                                                    <div className="">{data.customerNumber}</div>
                                                )}
                                            </span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">UTR Number</span>
                                            <span className="detail-value">{data.utr || "-"}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Created At</span>
                                            <span className="detail-value">{formatDateTime(data.createdAt)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Last Updated</span>
                                            <span className="detail-value">{formatDateTime(data.updatedAt)}</span>
                                        </div>
                                        {data.customerEmail && (
                                            <div className="detail-item">
                                                <span className="detail-label">Email</span>
                                                <span className="detail-value">{data.customerEmail}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="action-buttons">
                                    <div className="d-flex flex-wrap gap-2 justify-content-center">
                                        <button className="btn btn-custom btn-refresh" onClick={loadStatus}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="me-2">
                                                <path d="M1 4V10H7M23 20V14H17M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            Refresh Status
                                        </button>
                                        <Link className="btn btn-custom btn-home" to="/">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="me-2">
                                                <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            Go Home
                                        </Link>
                                        <Link className="btn btn-custom btn-wallet" to="/wallet">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="me-2">
                                                <path d="M21 12V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            My Wallet
                                        </Link>
                                        {String(data.status || "").toLowerCase() === "failed" && (
                                            <a
                                                className="btn btn-custom btn-support"
                                                href="https://t.me/CoinsUp01"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="me-2">
                                                    <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                Contact Support (Telegram)
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentStatus;


